import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  Building2,
  Clock,
  Home as HomeIcon,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Snowflake,
  UserRound,
} from "lucide-react";
import Seo from "@/components/Seo";
import Snowfall from "@/components/Snowfall";
import BeforeAfter from "@/components/BeforeAfter";
import AreaScanner from "@/components/AreaScanner";
import { MaskLines, Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import {
  CheckRow,
  CtaBand,
  Label,
  FaqBlock,
  Marquee,
  SectionHead,
} from "@/components/Shared";
import { GLOBAL_FAQ } from "@/pages/FaqPage";
import { BremenSkyline, Stadtmusikanten } from "@/components/graphics/Marks";
import { BRAND, DISTRICTS, HERO_SERVICES, IMG, SERVICE_INDEX } from "@/lib/site";

const PRIVAT = ["Schneeräumung", "Gehwege", "Eingänge", "Garagen", "Zufahrten"];

const PRIVAT_OBJECTS = [
  "Einfamilienhäuser",
  "Mehrfamilienhäuser",
  "Private Zufahrten",
  "Garagen & Stellplätze",
  "Gehwege",
  "Eingangsbereiche",
];

const GEWERBE_OBJECTS = [
  "Unternehmen",
  "Gewerbeimmobilien",
  "Parkplätze",
  "Parkhäuser & Parkdecks",
  "Zufahrten & Rampen",
  "Betriebsflächen",
  "Immobilienbestand",
];

const CASES = [
  {
    id: "parkflaeche",
    tab: "Parkfläche",
    title: "Große Parkfläche: von schneebedeckt zu vollständig geräumt",
    text:
      "Räumung der Fahrgassen und Stellplatzreihen, anschließend Streuen der Flächen. Der geräumte Schnee wird auf eine vorher festgelegte Lagerfläche abgelegt.",
    before: IMG.gritterParking,
    after: IMG.clearedParking,
    beforeAlt: "Verschneite Parkfläche in Bremen vor der Räumung, Streufahrzeug im Einsatz",
    afterAlt: "Vollständig geräumte und gestreute Parkfläche in Bremen nach dem Einsatz",
    facts: [
      { k: "Leistung", v: "Räumen + Streuen" },
      { k: "Objektart", v: "Gewerbliche Parkfläche" },
    ],
  },
  {
    id: "zufahrt",
    tab: "Zufahrt & Laderampe",
    title: "Zufahrt und Laderampe: wieder befahrbar für Anlieferung",
    text:
      "Räumung der Zufahrt auf voller Breite inklusive Rampenbereich, danach gezieltes Streuen der Gefällestrecke, damit Fahrzeuge sicher anfahren und bremsen können.",
    before: IMG.snowingRamp,
    after: IMG.clearedRamp,
    beforeAlt: "Verschneite Zufahrt mit Laderampe während des Schneefalls in Bremen",
    afterAlt: "Geräumte und gestreute Zufahrt mit Laderampe nach dem Winterdiensteinsatz",
    facts: [
      { k: "Leistung", v: "Räumen + Gefälle streuen" },
      { k: "Objektart", v: "Betriebszufahrt" },
    ],
  },
];
const GEWERBE = ["Parkplätze", "Gewerbeobjekte", "Zufahrten", "Außenflächen", "Immobilien"];

const Home = () => {
  const [caseIdx, setCaseIdx] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const skylineX = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <main data-testid="home-page">
      <Seo
        title="Winterdienst Bremen für Privat & Gewerbe | Bremer Schneemann"
        description="Winterdienst in Bremen und Umgebung: Schneeräumung, Streudienst und Eisbeseitigung für Gehwege, Zufahrten, Parkplätze und Immobilien. Persönlicher Ansprechpartner – jetzt anfragen."
        path="/"
        breadcrumbs={[{ label: "Startseite", to: "/" }]}
        service="Winterdienst Bremen"
        image={IMG.heroStreet}
      />

      {/* ======================= HERO ======================= */}
      <section
        ref={heroRef}
        data-testid="hero"
        style={{ position: "relative" }}
        className="overflow-hidden bg-white pb-16 pt-[116px] lg:pb-24 lg:pt-[176px]"
      >
        <div className="pointer-events-none absolute -left-32 top-10 h-[460px] w-[460px] rounded-full bg-[#EEF6FF] blur-3xl" />
        <motion.div style={{ x: skylineX }} className="pointer-events-none absolute inset-x-0 bottom-0">
          <BremenSkyline className="h-32 w-full opacity-[0.07] lg:h-44" color="#062B52" />
        </motion.div>

        <div className="shell relative z-10 grid gap-12 lg:grid-cols-[1.12fr_0.88fr] lg:gap-14">
          {/* ---------- LEFT ---------- */}
          <div className="min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Label>/ Winterdienst Bremen</Label>
            </motion.div>

            <h1 className="mt-5 text-[32px] font-extrabold leading-[1.07] sm:text-[44px] lg:text-[62px]">
              <MaskLines lines={["Bremer Schneemann:", "Ihr Winterdienst."]} delay={0.08} />
              <span className="mt-2 block text-[22px] font-bold leading-[1.18] text-[#10253A] sm:text-[29px] lg:text-[38px]">
                <MaskLines
                  lines={[
                    <>Schneeräumung &amp; Streudienst für</>,
                    <span key="pg" className="text-[#FF6B00]">Privat, Gewerbe &amp; Hausverwaltung.</span>,
                  ]}
                  delay={0.3}
                />
              </span>
            </h1>

            <motion.div
              className="mt-7 max-w-xl"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.62 }}
            >
              <p className="text-[15px] font-medium leading-relaxed text-[#10253A] sm:text-[17px]">
                Mit unserem Team und professionell ausgestatteten Radladern räumen und
                betreuen wir winterliche Flächen – vom privaten Grundstück bis zu größeren
                Gewerbe- und Wohnanlagen.
              </p>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#66788A] sm:text-[14.5px]">
                Ob Gehwege, Einfahrten, Zufahrten oder Gewerbeflächen: individuell
                abgestimmte Winterdienstlösungen für Bremen und die vereinbarten
                Einsatzgebiete. Planen Sie Ihren Winterdienst für die Saison 2026/2027
                frühzeitig.
              </p>
            </motion.div>

            <motion.div
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.74 }}
            >
              <Link
                to="/winterdienst-bremen-privat"
                data-testid="hero-cta-privat"
                className="btn btn-orange glow-accent h-14 px-6 text-[13.5px] tracking-[0.02em]"
              >
                PRIVATEN WINTERDIENST ANFRAGEN
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </Link>
              <Link
                to="/winterdienst-bremen-gewerbe-hausverwaltung"
                data-testid="hero-cta-gewerbe"
                className="btn btn-dark h-14 px-6 text-[13.5px]"
              >
                GEWERBE &amp; HAUSVERWALTUNG
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="hero-cta-primary"
                className="btn btn-ghost h-14 px-6 text-[13.5px]"
              >
                JETZT ANGEBOT ANFORDERN
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </Link>
            </motion.div>

            <motion.div
              className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.84 }}
            >
              <a
                href={BRAND.phoneHref}
                data-testid="hero-phone-link"
                className="flex items-center gap-2 text-[14px] font-bold text-[#0757B8] transition-colors hover:text-[#FF6B00]"
              >
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="flex items-center gap-2 text-[14px] font-semibold text-[#1fae56] transition-colors hover:text-[#FF6B00]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp: {BRAND.whatsappNumber}
              </a>
            </motion.div>

            <motion.ul
              className="mt-7 flex flex-wrap gap-x-7 gap-y-2.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              data-testid="hero-trust-row"
            >
              {["Bremen & Umgebung", "Privat & Gewerbe", "Persönlicher Ansprechpartner"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-[13px] font-medium text-[#10253A]">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
                    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#FF6B00" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t}
                </li>
              ))}
            </motion.ul>

            {/* ---- Privat / Gewerbe Panels ---- */}
            <motion.div
              className="mt-10 grid gap-3"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              data-testid="hero-panels"
            >
              <Link
                to="/winterdienst-bremen-privat"
                data-testid="hero-panel-privat"
                className="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-5 transition-all duration-500 hover:border-[#FF6B00] hover:bg-white hover:shadow-[0_30px_70px_-50px_rgba(6,43,82,0.55)] sm:flex-row sm:items-center sm:gap-6 sm:p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-[#0757B8] shadow-sm transition-colors group-hover:bg-[#FF6B00] group-hover:text-white">
                  <HomeIcon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-mono-label block text-[#0757B8]">Privat</span>
                  <span className="mt-1.5 block text-[16px] font-bold tracking-tight text-[#10253A] sm:text-[18px]" style={{ fontFamily: "Archivo, sans-serif" }}>
                    Winterdienst für Privathaushalte
                  </span>
                  <span className="mt-2 block text-[12.5px] leading-relaxed text-[#66788A]">
                    {PRIVAT.join(" · ")}
                  </span>
                </span>
                <span className="btn btn-ghost h-11 shrink-0 px-5 text-[13px] group-hover:border-[#FF6B00] group-hover:text-[#FF6B00]">
                  Für Privatkunden <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>

              <Link
                to="/winterdienst-bremen-gewerbe-hausverwaltung"
                data-testid="hero-panel-gewerbe"
                className="group relative flex flex-col gap-4 overflow-hidden rounded-2xl bg-[#062B52] p-5 transition-all duration-500 hover:shadow-[0_34px_80px_-46px_rgba(6,43,82,0.9)] sm:flex-row sm:items-center sm:gap-6 sm:p-6"
              >
                <Stadtmusikanten className="pointer-events-none absolute -right-2 -bottom-6 h-32 opacity-[0.1] transition-transform duration-700 group-hover:scale-110" color="#FFFFFF" />
                <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#FF6B00] transition-colors group-hover:bg-[#FF6B00] group-hover:text-white">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className="relative min-w-0 flex-1">
                  <span className="font-mono-label block text-[#FF6B00]">Gewerbe</span>
                  <span className="mt-1.5 block text-[16px] font-bold tracking-tight text-white sm:text-[18px]" style={{ fontFamily: "Archivo, sans-serif" }}>
                    Winterdienst für Unternehmen
                  </span>
                  <span className="mt-2 block text-[12.5px] leading-relaxed text-white/60">
                    {GEWERBE.join(" · ")}
                  </span>
                </span>
                <span className="btn relative h-11 shrink-0 border border-white/25 px-5 text-[13px] text-white group-hover:border-[#FF6B00] group-hover:bg-[#FF6B00]">
                  Für Gewerbekunden <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </motion.div>
          </div>

          {/* ---------- RIGHT ---------- */}
          <div className="min-w-0">
            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 26, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <Snowflake className="absolute -left-7 -top-7 hidden h-16 w-16 text-[#DCE5EF] lg:block" strokeWidth={1} />
              <Snowflake className="absolute -right-5 top-1/3 hidden h-10 w-10 text-[#DCE5EF] lg:block" strokeWidth={1} />

              <div
                data-testid="hero-service-box"
                className="grain relative overflow-hidden rounded-[22px] bg-[#0757B8] p-6 shadow-[0_40px_90px_-50px_rgba(7,87,184,0.85)] sm:p-8"
              >
                <Snowfall count={16} />
                <BremenSkyline className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full opacity-[0.16]" color="#FFFFFF" />
                <div className="relative z-10">
                  <Label tone="white">/ Leistungen</Label>
                  <h2
                    className="mt-3 text-[21px] font-bold leading-snug sm:text-[25px]"
                    style={{ color: "#fff" }}
                  >
                    Unser Winterdienst für Bremen
                  </h2>
                  <ul className="mt-6 space-y-3.5">
                    {HERO_SERVICES.map((s, i) => (
                      <motion.li
                        key={s}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
                      >
                        <span className="mt-0.5 inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-white">
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none">
                            <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#FF6B00" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <span className="text-[14px] leading-relaxed text-white/90 sm:text-[14.5px]">{s}</span>
                      </motion.li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-col gap-2.5 border-t border-white/15 pt-6 sm:flex-row">
                    <Link to="/leistungen" data-testid="hero-box-services-link" className="btn h-12 flex-1 bg-white px-5 text-[13.5px] text-[#062B52] hover:bg-[#EEF6FF]">
                      Alle Leistungen <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <a href={BRAND.phoneHref} data-testid="hero-box-phone" className="btn h-12 flex-1 border border-white/30 px-5 text-[13.5px] text-white hover:border-white">
                      <Phone className="h-4 w-4" /> Anrufen
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* hero image */}
            <motion.div
              className="relative mt-4 aspect-[4/3] overflow-hidden rounded-[22px]"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-image"
            >
              <img
                src={IMG.heroStreet}
                alt="Winterdienst im Einsatz: Streufahrzeug auf einer verschneiten Parkfläche in Bremen"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover object-center"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#062B52]/80 to-transparent" />
              <BremenSkyline className="pointer-events-none absolute inset-x-0 bottom-0 h-14 w-full opacity-45" color="#062B52" />
              <Stadtmusikanten className="pointer-events-none absolute bottom-2 right-4 h-20 opacity-55" color="#062B52" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5">
                <p className="font-mono-label text-white/85">Bremen · Winter · Winterdienst</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Schneeräumung",
          "Streudienst",
          "Eisbeseitigung",
          "Gehwege",
          "Zufahrten",
          "Parkplätze",
          "Parkhäuser",
          "Garagen",
          "Treppen",
          "24/7-Bereitschaft",
          "Bremen & Umgebung",
        ]}
      />

      {/* ======================= SECTION 1: Stadtgebiet-Scanner ======================= */}
      <section data-testid="section-scanner" className="bg-white py-20 lg:py-28">
        <div className="shell">
          <Reveal>
            <AreaScanner />
          </Reveal>
        </div>
      </section>

      {/* ======================= SECTION 2: Vorher / Nachher (kompakt) ======================= */}
      <section data-testid="section-beforeafter" className="border-y border-[#DCE5EF] bg-[#F5F8FC] py-14 lg:py-16">
        <div className="shell grid items-center gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
          <div>
            <Label>/ Referenzen</Label>
            <h2 className="mt-3 text-[22px] font-bold leading-[1.2] sm:text-[26px]">
              Vorher. Einsatz. Nachher.
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-[#66788A]">
              Echte dokumentierte Winterdiensteinsätze. Regler ziehen, um den Unterschied zu
              sehen.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="beforeafter-tabs">
              {CASES.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCaseIdx(i)}
                  data-testid={`beforeafter-tab-${c.id}`}
                  className={`rounded-full px-3.5 py-2 text-[12.5px] font-medium transition-colors ${
                    caseIdx === i
                      ? "bg-[#062B52] text-white"
                      : "border border-[#DCE5EF] bg-white text-[#66788A] hover:border-[#0757B8] hover:text-[#0757B8]"
                  }`}
                >
                  {c.tab}
                </button>
              ))}
            </div>
            <p className="mt-5 text-[13px] leading-relaxed text-[#66788A]">
              <span className="font-semibold text-[#10253A]">{CASES[caseIdx].facts[0].v}</span>
              {" · "}
              {CASES[caseIdx].facts[1].v}
            </p>
            <Link
              to="/kontakt"
              data-testid="beforeafter-cta"
              className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#0757B8] transition-colors hover:text-[#FF6B00]"
            >
              Jetzt Winterdienst anfragen <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mx-auto w-full max-w-[460px] lg:max-w-none">
            <BeforeAfter
              key={CASES[caseIdx].id}
              before={CASES[caseIdx].before}
              after={CASES[caseIdx].after}
              beforeAlt={CASES[caseIdx].beforeAlt}
              afterAlt={CASES[caseIdx].afterAlt}
            />
          </div>
        </div>
      </section>

      {/* ======================= SECTION 3: Privat & Gewerbe ======================= */}
      <section data-testid="section-segments" className="relative overflow-hidden bg-[#F5F8FC] py-20 lg:py-28">
        <div className="shell">
          <SectionHead
            label="/ Für wen wir arbeiten"
            title="Winterdienst passend zu Ihrem Objekt."
            text="Privat und Gewerbe stellen unterschiedliche Anforderungen an Technik, Zeitfenster und Dokumentation. Wählen Sie Ihren Bereich – der Ansprechpartner bleibt derselbe."
          />
          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            <Reveal>
              <article className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-[#DCE5EF] bg-white transition-shadow duration-500 hover:shadow-[0_40px_90px_-60px_rgba(6,43,82,0.65)]">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={IMG.houseEntrance}
                    alt="Wohnhaus mit geräumtem Weg zum Hauseingang im Winter"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-[center_62%] transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                  />
                  <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-3.5 py-1.5 font-mono-label text-[#0757B8] backdrop-blur-sm">
                    <HomeIcon className="h-3.5 w-3.5" /> Privat
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7 lg:p-8">
                  <h3 className="text-[21px] font-bold leading-snug sm:text-[24px]">
                    Winterdienst für Privatkunden
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-[#66788A]">
                    Gehweg geräumt, Hauseingang frei, Einfahrt befahrbar – zur Saison oder nur
                    für einzelne Zeiträume wie Urlaub, Krankheit oder Schichtdienst.
                  </p>
                  <p className="mt-6 font-mono-label text-[#66788A]">Für diese Objekte</p>
                  <ul className="mt-3.5 grid flex-1 gap-2.5 sm:grid-cols-2">
                    {PRIVAT_OBJECTS.map((p) => (
                      <CheckRow key={p}>{p}</CheckRow>
                    ))}
                  </ul>
                  <Link to="/winterdienst-bremen-privat" data-testid="segment-privat-cta" className="btn btn-dark mt-8 h-13 px-6 py-3.5 text-[13.5px]">
                    Winterdienst für Privat anfragen <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <article className="group relative flex h-full flex-col overflow-hidden rounded-[22px] bg-[#062B52] transition-shadow duration-500 hover:shadow-[0_40px_90px_-50px_rgba(6,43,82,0.9)]">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={IMG.clearedParking}
                    alt="Geräumter und gestreuter Parkplatz eines Gewerbeobjekts in Bremen"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top opacity-85 transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                  />
                  <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-[#FF6B00] px-3.5 py-1.5 font-mono-label text-white">
                    <Building2 className="h-3.5 w-3.5" /> Gewerbe
                  </span>
                </div>
                <Stadtmusikanten className="pointer-events-none absolute -right-10 bottom-0 h-56 opacity-[0.07]" color="#FFFFFF" />
                <div className="relative z-10 flex flex-1 flex-col p-7 lg:p-8">
                  <h3 className="text-[21px] font-bold leading-snug sm:text-[24px]" style={{ color: "#fff" }}>
                    Gewerblicher Winterdienst
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-white/70">
                    Räum- und Streuplan nach Ihren Betriebsabläufen: Prioritäten, Zeitfenster,
                    Schneelagerung – auf Wunsch mit Einsatzdokumentation.
                  </p>
                  <p className="mt-6 font-mono-label text-white/50">Für diese Objekte</p>
                  <ul className="mt-3.5 grid flex-1 gap-2.5 sm:grid-cols-2">
                    {GEWERBE_OBJECTS.map((p) => (
                      <CheckRow key={p} light>
                        {p}
                      </CheckRow>
                    ))}
                  </ul>
                  <Link to="/winterdienst-bremen-gewerbe-hausverwaltung" data-testid="segment-gewerbe-cta" className="btn btn-orange mt-8 h-13 px-6 py-3.5 text-[13.5px]">
                    Gewerblichen Winterdienst anfragen <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ======================= SECTION 3: Leistungen ======================= */}
      <section data-testid="section-services" className="bg-white py-20 lg:py-28">
        <div className="shell">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHead
              label="/ Leistungen"
              title="Die wichtigsten Leistungen im Überblick"
              text="Vom Gehweg bis zum Parkdeck – wir arbeiten flächengerecht mit dem passenden Gerät und dem richtigen Streumittel."
            />
            <Reveal delay={0.1}>
              <Link to="/leistungen" data-testid="services-all-cta" className="btn btn-ghost h-12 px-6 text-[13.5px]">
                Alle Leistungen ansehen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_INDEX.slice(0, 6).map((s, i) => (
              <StaggerItem key={s.to}>
                <Link
                  to={s.to}
                  data-testid={`service-card${s.to.replace(/\//g, "-")}`}
                  className="group flex h-full flex-col rounded-2xl border border-[#DCE5EF] bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#0757B8] hover:shadow-[0_34px_80px_-52px_rgba(6,43,82,0.6)]"
                >
                  <span className="font-mono-label text-[#DCE5EF] transition-colors group-hover:text-[#FF6B00]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-6 text-[18px] font-bold tracking-tight group-hover:text-[#0757B8]">{s.title}</p>
                  <p className="mt-3 flex-1 text-[13.5px] leading-relaxed text-[#66788A]">{s.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0757B8]">
                    Mehr erfahren
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ======================= SECTION 4: Warum ======================= */}
      <section data-testid="section-why" className="relative overflow-hidden bg-[#062B52] py-20 text-white lg:py-28">
        <Snowfall count={20} />
        <Stadtmusikanten className="pointer-events-none absolute -right-10 bottom-0 h-[420px] opacity-[0.07]" color="#FFFFFF" />
        <div className="shell relative z-10 grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-20">
          <div>
            <SectionHead
              label="/ Warum Bremer Schneemann"
              title="Ein Bremer Betrieb mit einem Gesicht – nicht mit einer Hotline."
              text="Inhaber Adil Altun plant die Einsätze selbst und bleibt Ihr Ansprechpartner über die gesamte Saison. Kurze Absprachen, klare Zuständigkeit, keine Weiterleitung."
              light
            />
            <Reveal delay={0.15}>
              <div className="mt-9 overflow-hidden rounded-2xl">
                <img
                  src={IMG.plowStreet}
                  alt="Winterdienst räumt eine verschneite Straße am frühen Morgen"
                  loading="lazy"
                  className="h-56 w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <Stagger className="grid gap-4 sm:grid-cols-2">
            {[
              { icon: UserRound, t: "Persönlicher Ansprechpartner", d: "Sie sprechen immer mit derselben Person – Inhaber Adil Altun." },
              { icon: MapPin, t: "Bremen & Umgebung", d: "Dichte Touren im Stadtgebiet statt Fahrzeiten quer durchs Land." },
              { icon: Building2, t: "Private und gewerbliche Objekte", d: "Einfamilienhaus, Wohnanlage, Betriebshof – aus einer Hand." },
              { icon: Snowflake, t: "Winterdienst nach Bedarf", d: "Saisonbetreuung oder Einzeleinsätze – Sie legen den Umfang fest." },
            ].map((v) => (
              <StaggerItem key={v.t}>
                <div className="flex h-full flex-col rounded-2xl border border-white/12 bg-white/[0.04] p-6 transition-colors duration-500 hover:border-[#FF6B00]/50 hover:bg-white/[0.08]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6B00] text-white">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <p className="mt-5 text-[16.5px] font-bold tracking-tight" style={{ color: "#fff" }}>{v.t}</p>
                  <p className="mt-2.5 text-[13.5px] leading-relaxed text-white/65">{v.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ======================= SECTION 5: Bremen-Bezug ======================= */}
      <section data-testid="section-area" className="bg-white py-20 lg:py-24">
        <div className="shell grid items-center gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <Reveal>
            <div className="overflow-hidden rounded-[22px]">
              <img
                src={IMG.stadtmusikanten}
                alt="Die Bremer Stadtmusikanten am Rathaus im Schnee"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <SectionHead
              label="/ Bremen"
              title="Aus Bremen – für Bremen."
              text="Hauptbüro an der Flughafenallee 26 auf der Überseeinsel, Fuhrpark-Start in Bremen-Hemelingen. Kurze Wege, dichte Touren und ein Ansprechpartner, der die Stadt kennt."
            />
            <Reveal delay={0.12}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <Link to="/einsatzgebiet" data-testid="area-cta" className="btn btn-dark h-13 py-3.5 text-[13.5px]">
                  Einsatzgebiet ansehen <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/winterdienst-bremen-umgebung" data-testid="area-umgebung-cta" className="btn btn-ghost h-13 py-3.5 text-[13.5px]">
                  Bremen &amp; Umgebung
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ======================= Referenzen / Erfahrung ======================= */}
      <section data-testid="section-referenz" className="border-y border-[#DCE5EF] bg-[#F5F8FC] py-16 lg:py-20">
        <div className="shell grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16 lg:items-center">
          <SectionHead
            label="/ Erfahrung"
            title="Erfahrung aus Aufträgen für gewerbliche Auftraggeber."
            text="Neben privaten und gewerblichen Objekten in Bremen führen wir Dienstleistungen auch im Auftrag anderer Unternehmen aus – unter anderem als Subunternehmer für die Deutsche Bahn."
          />
          <Reveal delay={0.1}>
            <div className="rounded-[22px] border border-[#DCE5EF] bg-white p-7 sm:p-8">
              <p className="font-mono-label text-[#0757B8]">/ Einsätze im Auftrag von Unternehmen</p>
              <ul className="mt-5 grid gap-3">
                <CheckRow>Tätigkeit als Subunternehmer für die Deutsche Bahn</CheckRow>
                <CheckRow>Abgestimmte Abläufe und feste Zeitfenster</CheckRow>
                <CheckRow>Dokumentation der ausgeführten Leistungen auf Wunsch</CheckRow>
                <CheckRow>Erfahrung mit Verwaltungen und Gewerbeobjekten</CheckRow>
              </ul>
              <p className="mt-6 border-t border-[#DCE5EF] pt-5 text-[12px] leading-relaxed text-[#66788A]">
                Hinweis: Es handelt sich um eine Auftragsbeziehung als Subunternehmer – keine
                offizielle Partnerschaft, Zertifizierung oder Empfehlung durch den Auftraggeber.
                Marken und Logos Dritter werden hier nicht verwendet.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqBlock
        items={GLOBAL_FAQ.slice(0, 6)}
        title="Häufige Fragen zum Winterdienst in Bremen"
        label="/ FAQ"
        moreTo="/faq"
      />

      <CtaBand />
    </main>
  );
};

export default Home;
